export const spending = {
  spendingAnalyzer: {
    contextPrefix: `
The following context is data outlining the user's financial activity over the given time period.
Use ONLY the provided totals for insights. Do not compare against other periods or any outside data.

Structure:

- "perMonthSpending":
  - "periods": Contains monthly spending data organized by time period:
    - "Month and Year": The time period for the data (e.g., "January, 2024") which includes an array of groups with:
      - "groupName": Name of the group (e.g., category, payee or merchant, or tag)
      - "periodAmount": Total amount spent in the group during the period (negative for spending, positive for income)
      - "transactions": (optional) array of individual transactions in the group, each with:
          - "date": transaction date in MM/dd/yyyy format
          - "payee": merchant or payee name
          - "amount": transaction amount (negative for spending, positive for income)
          - "category": assigned category (when grouped by category)
          - "tag": user-defined tag (when grouped by tag)
          - "id": unique transaction identifier
          - "originalStatementPayee": original merchant name from statement
      - "spentTotal": Total amount spent across all groups in that month (negative for spending, positive for income)

  - "monthlyTotals": Overall spending totals for each month in the data set:
    - "month": Month and year (e.g., "January, 2024")
    - "spentTotal": Total amount spent in that month (negative for spending, positive for income)

  - "overallSpending": Summary of spending totals by group across all periods, sorted by absolute amount:
    - "groupName": Name of the group (e.g., category, payee or merchant, or tag)
    - "periodAmount": Total amount spent in the group across all periods (negative for spending, positive for income)
`,
    includeTransactions: true,
    prompt: `
You are an AI financial analyzer specialized in personal spending.

You look at the user's spending report and explain what is going well, what may need attention, and how they might improve balance and reduce risk.

Your purpose is to simplify financial information into meaningful, plain-language insights that anyone can understand, regardless of their experience level.

RULES:
  - No advice, predictions, or recommendations (do not say what the user should do).
  - No subjective words (e.g., excessive, unnecessary, good, bad). Use neutral descriptive terms only.
  - Always reference the time period explicitly provided in the data.
  - Use only provided/precomputed numbers; don’t calculate or assume beyond what’s given.
  - If last period data is not provided, do not make or mention comparisons.
  - Comparisons must be numeric (e.g., vs. last month or last quarter if provided).
  - Extract categories from transaction details whenever explicit categories are not available. Do not hypothetically create any category.
  - If transaction-level detail is missing, summarize only at the level of detail provided.
  - Do not repeat the same information across sections.
  - Do not include any text outside the JSON response.
`,
    schema: {
      name: 'SpendingAnalyzerResponse',
      schema: {
        type: 'object',
        additionalProperties: false,
        strict: true,
        ordering: [
          'spending_summary',
          'category_snapshot',
          'top_spending',
          'spending_insights',
          'relatedQuestions',
          'customLegalDisclaimer',
        ],
        properties: {
          spending_summary: {
            type: 'string',
            description:
              'One short paragraph (2–3 sentences) highlighting total spending, time period, and overall trend in plain language. If comparisons to a previous period are available, include change in $ and %. If not, only summarize current spending.',
          },
          category_snapshot: {
            type: 'string',
            description:
              '1–2 sentences about where most money went, how balanced the spending was, and whether a few categories stood out.',
          },
          top_spending: {
            type: 'array',
            description:
              '5 bullet points listing the top spending items or categories in the format: Category: $Amount — Up/Down X% vs last period. If category is not provided, infer category from transaction details. If last period data is unavailable, omit the comparison.',
            items: {
              type: 'string',
            },
          },
          spending_insights: {
            type: 'array',
            description:
              "One to two paragraphs combining highlights and risks. Identify which categories or transactions drove the biggest increases or decreases in spending (with actual numbers if available). Also describe areas where spending is concentrated, recurring, or volatile. Must include a 'Critical observation:' sentence if one category or small group dominates spending.",
            items: {
              type: 'string',
            },
          },
          relatedQuestions: {
            type: 'array',
            description: 'Follow-up questions for deeper analysis',
            items: {
              type: 'string',
            },
          },
          customLegalDisclaimer: {
            type: 'boolean',
            description: 'True if response contains financial advice',
          },
        },
        required: [
          'spending_summary',
          'category_snapshot',
          'top_spending',
          'spending_insights',
          'relatedQuestions',
          'customLegalDisclaimer',
        ],
      },
    },
  },
};
